/** Utility functions for task rebalancing, separated from React hook logic. */
// @ts-check
import { db, Timestamp, runTransaction } from '@/lib/firebase';
import { generateKeyBetween } from 'fractional-indexing';
import { updateChildrenPathsRecursively } from '@/features/tasks/utils/structure/taskStructureUtils';
import { getTaskBatch, commitBatch } from '@/services/taskMutations';
import {
  getTaskRef,
  getOrderAnchorRef,
  reserveNextOrderKeyInTransaction,
} from '@/services/taskService';

/**
 * @param {Task[]} tasks
 * @param {string | null} parentId
 */
export const getSiblingsToRebalance = (tasks, parentId) =>
  tasks
    .filter((t) => (t.parentId || null) === (parentId || null))
    .sort((a, b) => {
      // @ts-ignore - Handle various date formats gracefully in rebalance
      const timeA = Number(
        a.createdAt?.getTime ? a.createdAt.getTime() : a.createdAt || 0,
      );
      // @ts-ignore
      const timeB = Number(
        b.createdAt?.getTime ? b.createdAt.getTime() : b.createdAt || 0,
      );
      return timeA - timeB;
    });

/**
 * @param {Map<string, Task>} taskMap
 * @param {string | null} parentId
 * @returns {string | null}
 */
export const getParentPath = (taskMap, parentId) => {
  if (!parentId) return '';
  const parent = taskMap.get(parentId);
  if (parentId && (!parent || !parent.materializedPath)) {
    console.error(
      `REBALANCE ERROR: Parent task ${parentId} not found or has invalid path.`,
    );
    return null;
  }
  return parent?.materializedPath ?? '';
};

/**
 * @param {import('firebase/firestore').Transaction} transaction
 * @param {Task[]} siblings
 * @param {string} parentPath
 * @param {string} firstKey
 */
const rebalanceSiblingsInTransaction = (
  transaction,
  siblings,
  parentPath,
  firstKey,
) => {
  let lastKey = firstKey;
  let hasKeyChanges = false;
  /** @type {Array<{id: string, newKey: string, newPath: string}>} */
  const updates = [];

  for (let i = 0; i < siblings.length; i++) {
    const sibling = siblings[i];
    const newKey = i === 0 ? firstKey : generateKeyBetween(lastKey, undefined);
    const newPath = `${parentPath}${newKey}.`;
    updates.push({ id: sibling.id, newKey, newPath });
    if (sibling.orderKey !== newKey || sibling.materializedPath !== newPath) {
      hasKeyChanges = true;
      transaction.update(getTaskRef(sibling.id), {
        orderKey: newKey,
        materializedPath: newPath,
        updatedAt: Timestamp.now(),
      });
    }
    lastKey = newKey;
  }

  return { updates, lastKey, hasKeyChanges };
};

/** @param {(useAnchor: boolean) => Promise<void>} rebalanceFn */
const runRebalanceWithAnchorFallback = async (rebalanceFn) => {
  try {
    await rebalanceFn(true);
  } catch (error) {
    if (error?.code !== 'permission-denied') throw error;
    console.warn('REBALANCE: taskOrderAnchors denied, retrying without anchor');
    await rebalanceFn(false);
  }
};

/**
 * Rebalances all siblings under a parent by assigning fresh orderKeys inside
 * a Firestore transaction. Uses the per-parent orderKey anchor
 * (reserveNextOrderKeyInTransaction) to serialize concurrent rebalances of
 * the same parent group, preventing duplicate orderKey generation.
 *
 * The anchor is read/written once at the start of the transaction, then each
 * sibling's key is generated sequentially from the previous one (no
 * additional Firestore reads per sibling). The anchor is updated to the
 * final key at the end of the loop.
 *
 * Caveat: a full-rebalance transaction touches N sibling docs plus the
 * anchor. Firestore transactions have a 500-document limit. No group is
 * remotely close to that today, but flag this if handleRebalance ever gets
 * used on bulk imports.
 *
 * @param {string} taskId
 * @param {Task[]} tasks
 */
export const executeRebalance = async (taskId, tasks) => {
  /** @type {Map<string, Task>} */
  const taskMap = new Map(tasks.map((t) => [t.id, t]));
  const taskToFix = taskMap.get(taskId);
  if (!taskToFix) {
    console.error(
      'REBALANCE ERROR: Starting task not found in provided task list.',
    );
    return;
  }

  const { parentId, userId } = taskToFix;
  const siblings = getSiblingsToRebalance(tasks, parentId);
  if (siblings.length <= 1) return;

  const parentPath = getParentPath(taskMap, parentId);
  if (parentPath === null) return;

  // Bootstrap: read the current last sibling's orderKey as a fallback hint
  // for the anchor. This is a plain (non-transactional) read, so the very
  // first rebalance of a parent that has never used the anchor can still
  // race once — a narrow, one-time window that self-heals as soon as the
  // anchor doc exists.
  const lastSibling = siblings[siblings.length - 1];
  const fallbackLastKey = lastSibling?.orderKey || null;

  /** @type {Array<{id: string, newKey: string, newPath: string}>} */
  let siblingUpdates = [];

  const rebalanceInTransaction = async (useAnchor) => {
    await runTransaction(db, async (transaction) => {
      await Promise.all(siblings.map((s) => transaction.get(getTaskRef(s.id))));

      const firstKey = useAnchor
        ? await reserveNextOrderKeyInTransaction(
            transaction,
            parentId || null,
            userId,
            fallbackLastKey,
          )
        : generateKeyBetween(fallbackLastKey, null);

      const { updates, lastKey, hasKeyChanges } =
        rebalanceSiblingsInTransaction(
          transaction,
          siblings,
          parentPath,
          firstKey,
        );

      if (hasKeyChanges && useAnchor) {
        const anchorRef = getOrderAnchorRef(userId, parentId || null);
        transaction.set(anchorRef, {
          lastOrderKey: lastKey,
          userId,
          parentId: parentId || null,
          updatedAt: Timestamp.now(),
        });
      }

      siblingUpdates = updates;
    });
  };

  await runRebalanceWithAnchorFallback(rebalanceInTransaction);

  // Children path updates happen outside the transaction via a batch.
  // The transaction already committed the sibling orderKey/path changes,
  // so we propagate the new paths to descendants.
  const batch = getTaskBatch();
  let hasChanges = false;

  for (const { id, newKey, newPath } of siblingUpdates) {
    const sibling = taskMap.get(id);
    if (
      sibling &&
      (sibling.orderKey !== newKey || sibling.materializedPath !== newPath)
    ) {
      hasChanges = true;
      await updateChildrenPathsRecursively(id, newPath, userId, batch);
    }
  }

  if (hasChanges) {
    try {
      await commitBatch(batch);
    } catch (e) {
      console.error('REBALANCE FIRESTORE ERROR:', e);
      throw e;
    }
  }
};
